import { Card, Form, PageHeader, Button, Typography } from 'antd'

import FormBlock from '../../components/forms/FormBlock'
import FormWrap from '../../components/forms/FormWrap'
import {
  FixedDesignDescriptionForm,
  FixedDesignOptionsForm,
} from '../../forms/FixedDesign'
import { createFixedDesign } from '../../network/FixedDesign'
import { createUrlWithFiltersFromCurrentURL } from '../../utils/filterUtils'
import { useMemo, useState, useEffect } from 'react'
import cloneDeep from 'lodash.clonedeep'
import { formOptions } from '../../forms/blocks'
import { useAuth } from '../../hooks/useAuth'
import { validateOptionSubOptionRefs } from './utils/optionValidators'

/** Get default field values for a category */
function getDetailDefaults(options, categoryKey) {
  const opt = options[categoryKey]
  if (!opt?.default?.orderCustom || !opt?.forms?.[0]?.key) return {}
  const formKeyPath = opt.forms[0].key
  const formKey = formKeyPath.split('.').pop()
  const defaults = opt.default.orderCustom[formKey]
  return defaults ? cloneDeep(defaults) : {}
}

export const Details = ({ form }) => {
  const options = useMemo(() => {
    const opts = cloneDeep(formOptions)
    delete opts.collection
    delete opts.suit_2_pieces
    delete opts.suit_3_pieces
    delete opts.default
    return opts
  }, [])

  const DetailItem = ({ field, fieldIndex, remove, fields = [] }) => {
    const [index, setIndex] = useState(-1)
    const [key, setKey] = useState(null)
    const [template, setTemplate] = useState(null)
    const [isInitialized, setIsInitialized] = useState(false)
    
    const detailValue = Form.useWatch(['details', fieldIndex], form)

    useEffect(() => {
      // Product detail fields are never mandatory — strip all required rules so the
      // form can always be saved regardless of display state or field values.
      const applyRequiredRules = (fieldsArr) => {
        if (!Array.isArray(fieldsArr)) return
        fieldsArr.forEach((f) => {
          if (f.isRequired) {
            f.rules = []
          } else if (f.rules && Array.isArray(f.rules)) {
            f.rules = f.rules.map((rule) => {
              if (rule && typeof rule === 'object') {
                const { required, ...rest } = rule
                return rest
              }
              return rule
            }).filter(Boolean)
          }
        })
      }

      if (key && options[key]) {
        const tpl = cloneDeep(options[key].forms[0].form[0])
        for (let i in tpl.sections) {
          const section = tpl.sections[i]
          const numColumns = section.columns || 2
          for (let j in section.fields) {
            section.fields[j].key.splice(0, 0, key)
            section.fields[j].key.splice(0, 0, fieldIndex)
          }
          // Display column: one header "Display" in row 0, then switch + field per row (no label on each switch)
          section.columns = 1 + numColumns
          const newFields = []
          // Column header in row 1, col 1
          newFields.push({
            type: 'columnHeader',
            label: 'Display',
            key: ['_displayColumnLabel', i],
            pos: { col: 1, row: 1, size: 1 },
          })
          let row = 2
          for (let j = 0; j < section.fields.length; j++) {
            const field = section.fields[j]
            const leafKey = field.key[field.key.length - 1]
            const displayField = {
              type: 'switch',
              key: ['_displayFlags', leafKey],
              label: '',
              default: false,
              checkedChildren: 'show',
              uncheckedChildren: 'hide',
              pos: { col: 1, row, size: 1 },
              valuePropName: 'checked',
            }
            displayField.key.splice(0, 0, key)
            displayField.key.splice(0, 0, fieldIndex)
            newFields.push(displayField)
            field.pos = { col: 2, row, size: numColumns }
            newFields.push(field)
            row += 1
          }
          section.fields = newFields
          section.wrapperClassName = 'nb-columns-narrow-first nb-columns-display-first'
          applyRequiredRules(section.fields)
        }

        setTemplate(tpl)
      } else {
        setTemplate(null)
      }
    }, [fieldIndex, key])

    useEffect(() => {
      if (index >= 0) {
        setKey(Object.keys(options)[index])
      } else {
        setKey(null)
      }
    }, [index])

    // Detect product type from form data (restore selection when form has existing data)
    useEffect(() => {
      const detail = form.getFieldValue(['details', fieldIndex])
      
      if (detail && Object.keys(detail).length > 0) {
        const detailKeys = Object.keys(detail).filter(k => k !== '_id')
                
        const foundIndex = Object.keys(options).findIndex(
          (key) => detailKeys.includes(key) && !!detail[key] && Object.keys(detail[key]).length > 0
        )
        
        if (foundIndex >= 0) {
          const foundKey = Object.keys(options)[foundIndex]
          setIndex(foundIndex)
          setKey(foundKey)
        }
      }
      if (!isInitialized) {
        setIsInitialized(true)
      }
    }, [fieldIndex, detailValue, isInitialized])

    return (
      <>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            width: 'fit-content',
            columnGap: 12,
            rowGap: 12,
          }}
        >
          {/* According to the selected options, render different forms */}
          {Object.keys(options).map((key, i) => (
            <div
              key={i}
              className={`order-btn big ${index === i ? 'selected' : ''}`}
              onClick={() => {
                const selectedKey = Object.keys(options)[i]
                // Set the detail object with category defaults so isHiding (e.g. contrastColor) works
                const defaults = getDetailDefaults(options, selectedKey)
                form.setFieldValue(['details', fieldIndex], { [selectedKey]: defaults })
                setIndex(i)
                setKey(selectedKey)
              }}
            >
              {options[key].name}
            </div>
          ))}
          {fields.length > 1 && (
            <Button
              type="ghost"
              onClick={() => remove(fieldIndex)}
              style={{
                width: '100%',
                height: '100%',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: 5,
                color: '#9e1313',
                border: '1px solid #d1abab',
                '&:hover': {
                  background: '#d1abab',
                },
              }}
            >
              Delete
            </Button>
          )}
        </div>
        {template && (
          <FormBlock
            form={form}
            formTemplate={template || options[key].forms[0].form[0]}
          />
        )}
      </>
    )
  }

  const { Title } = Typography

  return (
    <Card
      className="form-wrapper-card"
      style={{ height: 'fit-content', flex: 1 }}
      title={<Title level={4}>Category</Title>}
    >
      <Form.Item
        name="details"
        rules={[
          {
            validator: (_, value) => {
              // Allow empty details array or empty objects - fields are optional in fixed design create/edit
              if (!value || !Array.isArray(value) || value.length === 0) {
                return Promise.resolve() // Allow empty - fields are optional
              }
              const detail = value[0]
              if (!detail || typeof detail !== 'object') {
                return Promise.resolve() // Allow empty - fields are optional
              }
              const optionKeys = Object.keys(detail || {}).filter((k) => k !== '_id')
              // If there are any option keys (category selected), that's valid
              // Empty objects are also valid (category selected but no fields filled)
              if (optionKeys.length > 0) {
                return Promise.resolve()
              }
              // Even if no keys, allow it - fields are optional
              return Promise.resolve()
            },
          },
        ]}
        style={{ marginBottom: 0 }}
      >
        <Form.List name="details">
          {(fields, { add, remove }) => (
            <div className="section-from-vertical">
              {fields.map((field, fieldIndex) => (
                <DetailItem
                  key={field.key}
                  form={form}
                  field={field}
                  fieldIndex={fieldIndex}
                  remove={remove}
                  fields={fields}
                />
              ))}
            </div>
          )}
        </Form.List>
      </Form.Item>
    </Card>
  )
}

const CreateFixedDesign = ({ history }) => {
  const [form] = Form.useForm()
  const { user } = useAuth()

  return (
    <>
      <PageHeader
        className="site-page-header"
        title="Create fixed design"
        onBack={() => history.push(createUrlWithFiltersFromCurrentURL('/fixed-designs'))}
        style={{ backgroundColor: '#fff' }}
      />
      <FormWrap
        form={form}
        data={{ details: [{}] }}
        title="create_fixed_design"
        request={createFixedDesign}
        cancelText="Cancel"
        okText="Save"
        onSuccess={() => history.push(createUrlWithFiltersFromCurrentURL('/fixed-designs'))}
        onCancel={() => history.push(createUrlWithFiltersFromCurrentURL('/fixed-designs'))}
        cleanBeforeSending={(oldData) => {
          const payload = {
            name: oldData.name || {},
            sku: oldData.sku,
            description: oldData.description || {},
            category: oldData.category,
            store: user?.currentStore,
            options: (oldData.options || []).map((opt) => ({
              name: opt.name || {},
              freeField: !!opt.freeField,
              subOptions: (opt.subOptions || []).map((sub) => ({
                name: sub.name || {},
                ref: sub.ref,
              })),
            })),
            details: oldData.details || [],
          }
          return payload
        }}
        invalidate={['getFixedDesigns']}
      >
        <div className="section-from-vertical">
          <FormBlock form={form} formTemplate={FixedDesignDescriptionForm} />
          <Details form={form} />
          <Form.Item
            name="options"
            rules={[{ validator: validateOptionSubOptionRefs }]}
          >
            <FormBlock form={form} formTemplate={FixedDesignOptionsForm} />
          </Form.Item>
        </div>
      </FormWrap>
    </>
  )
}

export default CreateFixedDesign
